/* ============================================================
   AK Spotlight — deep-cut introduction controller

   Surfaces one overlooked product (from the chosen collection)
   at a well-judged moment. Everything about WHEN it appears is
   controlled from Theme settings > Spotlight and arrives here as
   data-* attributes on #AkSpotlight:

     - data-trigger:        both | dwell | scroll | exit
     - data-dwell-seconds:  pause before showing
     - data-scroll:         scroll-depth %% before showing
     - data-returning-only: 1 = stay silent on a first session
     - data-cooldown-hours: minimum gap between appearances
     - data-dismiss-days:   pause after the visitor closes it

   It never introduces a product the visitor is currently viewing
   or has recently viewed, and shows at most once per session.

   Storage is wrapped in try/catch — Safari private mode throws on
   access, and a missing store should silently disable the card,
   never break the page. No backdrop, no scroll lock: this is an
   aside, not a modal.
   ============================================================ */
(function () {
  if (window.__akSpotlightInit) return;
  window.__akSpotlightInit = true;

  var RECENT_MAX = 20;

  var STATE_KEY = 'ak_spotlight_state';
  var RECENT_KEY = 'ak_recent_products';
  var SESSION_KEY = 'ak_spotlight_shown_session';
  var SESSION_MARK = 'ak_spotlight_session_mark';
  var SESS_COUNT_KEY = 'ak_spotlight_sessions';

  var el = null;
  var cfg = null;
  var fired = false;
  var atcVisible = false;   /* sticky add-to-cart bar currently on screen? */

  /* Preview override: add ?spotlight=preview to any URL (or the
     #spotlight-preview hash) to force the card to appear immediately,
     bypassing every frequency cap and the engagement trigger. It does
     NOT record a "shown" timestamp, so it won't start a cooldown —
     handy for design/QA in the theme editor or a normal browser. */
  var PREVIEW = false;
  try {
    PREVIEW = /[?&]spotlight=preview(\b|&|$)/.test(window.location.search) ||
      window.location.hash === '#spotlight-preview';
  } catch (e) { PREVIEW = false; }

  /* ── Safe storage helpers ─────────────────────────────── */

  function lsGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function lsSet(key, val) {
    try { window.localStorage.setItem(key, val); } catch (e) { /* noop */ }
  }
  function ssGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function ssSet(key, val) {
    try { window.sessionStorage.setItem(key, val); } catch (e) { /* noop */ }
  }
  function readJSON(raw, fallback) {
    if (!raw) return fallback;
    try { return JSON.parse(raw); } catch (e) { return fallback; }
  }
  function num(val, fallback) {
    var n = parseFloat(val);
    return isNaN(n) ? fallback : n;
  }

  /* ── Read settings off the element ────────────────────── */

  function readConfig() {
    var d = el.dataset || {};
    return {
      currentHandle: d.currentHandle || '',
      eyebrow: d.eyebrow || '',
      trigger: d.trigger || 'both',
      dwellMs: num(d.dwellSeconds, 30) * 1000,
      scrollPct: num(d.scroll, 55),
      returningOnly: d.returningOnly === '1',
      cooldownMs: num(d.cooldownHours, 48) * 60 * 60 * 1000,
      dismissMs: num(d.dismissDays, 14) * 24 * 60 * 60 * 1000
    };
  }

  /* ── Session counting (for "returning visitors only") ──── */

  function sessionCount() {
    if (!ssGet(SESSION_MARK)) {
      ssSet(SESSION_MARK, '1');
      var c = parseInt(lsGet(SESS_COUNT_KEY) || '0', 10) + 1;
      lsSet(SESS_COUNT_KEY, String(c));
    }
    return parseInt(lsGet(SESS_COUNT_KEY) || '0', 10);
  }

  /* ── Recently-viewed tracking ─────────────────────────── */

  function recordCurrentProduct(currentHandle) {
    if (!currentHandle) return;
    var list = readJSON(lsGet(RECENT_KEY), []);
    list = list.filter(function (h) { return h !== currentHandle; });
    list.unshift(currentHandle);
    if (list.length > RECENT_MAX) list = list.slice(0, RECENT_MAX);
    lsSet(RECENT_KEY, JSON.stringify(list));
  }

  /* ── Frequency gate ───────────────────────────────────── */

  function allowedToShow(sessions) {
    if (ssGet(SESSION_KEY)) return false;            /* already shown this session */
    if (cfg.returningOnly && sessions < 2) return false; /* first session: stay quiet */
    var state = readJSON(lsGet(STATE_KEY), {});
    var now = Date.now();
    if (state.dismissedAt && now - state.dismissedAt < cfg.dismissMs) return false;
    if (state.shownAt && now - state.shownAt < cfg.cooldownMs) return false;
    return true;
  }

  function markShown() {
    ssSet(SESSION_KEY, '1');
    var state = readJSON(lsGet(STATE_KEY), {});
    state.shownAt = Date.now();
    lsSet(STATE_KEY, JSON.stringify(state));
  }

  function markDismissed() {
    var state = readJSON(lsGet(STATE_KEY), {});
    state.dismissedAt = Date.now();
    lsSet(STATE_KEY, JSON.stringify(state));
  }

  /* ── Choose which product to introduce ────────────────── */

  function chooseProduct(items, currentHandle) {
    if (!items || !items.length) return null;
    var recent = readJSON(lsGet(RECENT_KEY), []);
    var exclude = {};
    if (currentHandle) exclude[currentHandle] = true;
    recent.forEach(function (h) { exclude[h] = true; });

    var eligible = items.filter(function (it) { return it.handle && !exclude[it.handle]; });
    /* If everything has been seen, fall back to anything but the
       product on screen — better a familiar deep cut than nothing. */
    if (!eligible.length) {
      eligible = items.filter(function (it) {
        return it.handle && it.handle !== currentHandle;
      });
    }
    if (!eligible.length) return null;
    return eligible[Math.floor(Math.random() * eligible.length)];
  }

  /* ── Populate + reveal ────────────────────────────────── */

  function populate(item) {
    var link = el.querySelector('[data-spotlight-link]');
    var img = el.querySelector('[data-spotlight-img]');
    var titleEl = el.querySelector('[data-spotlight-title]');
    var cta = el.querySelector('[data-spotlight-cta]');
    var ctaLabel = el.querySelector('[data-spotlight-cta-label]');

    if (link) link.href = item.url;
    if (cta) cta.href = item.url;
    if (titleEl) titleEl.textContent = item.title;

    if (img) {
      img.src = item.img || '';
      img.alt = cfg.eyebrow ? (cfg.eyebrow + ': ' + item.title) : item.title;
    }

    if (ctaLabel) ctaLabel.textContent = 'See it';
  }

  function reveal(skipMark) {
    el.hidden = false;
    el.setAttribute('aria-hidden', 'false');
    /* Force reflow so the transition runs from the hidden state. */
    /* eslint-disable-next-line no-unused-expressions */
    el.offsetHeight;
    el.classList.add('is-visible');
    applySuppression();
    if (!skipMark) markShown();
  }

  function dismiss() {
    el.classList.remove('is-visible');
    el.setAttribute('aria-hidden', 'true');
    markDismissed();
    setTimeout(function () {
      if (!el.classList.contains('is-visible')) el.hidden = true;
    }, 600);
  }

  /* ── Sticky add-to-cart coexistence ───────────────────────
     The PDP sticky bar is a <sticky-cart> element that gains the
     class "is-visible" (slides up) once the buy box scrolls off.
     Two fixed cards in the same corner is clutter, and on a PDP
     the buy nudge should win — so we step the spotlight aside
     while the bar is up and bring it back when it retracts. */

  function applySuppression() {
    if (!el || !el.classList.contains('is-visible')) return;
    el.classList.toggle('is-suppressed', atcVisible);
  }

  function watchStickyCart() {
    var bar = document.querySelector('sticky-cart');
    if (!bar) return;
    function update() {
      atcVisible = bar.classList.contains('is-visible');
      applySuppression();
    }
    update();
    try {
      var obs = new MutationObserver(update);
      obs.observe(bar, { attributes: true, attributeFilter: ['class'] });
    } catch (e) { /* noop */ }
  }

  /* ── Engagement trigger ───────────────────────────────── */

  function armTrigger(onEngage) {
    var done = false;
    var dwellTimer = null;

    function cleanup() {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseout', onMouseOut);
      if (dwellTimer) clearTimeout(dwellTimer);
    }
    function fire() {
      if (done) return;
      done = true;
      cleanup();
      onEngage();
    }
    function onScroll() {
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      var pct = (window.scrollY || doc.scrollTop) / scrollable * 100;
      if (pct >= cfg.scrollPct) fire();
    }
    function onMouseOut(e) {
      /* Exit intent: pointer leaves through the top of the viewport. */
      if (!e.relatedTarget && (e.clientY == null || e.clientY <= 0)) fire();
    }

    var mode = cfg.trigger;
    var noHover = false;
    try { noHover = window.matchMedia('(hover: none)').matches; } catch (e) { noHover = 'ontouchstart' in window; }

    if (mode === 'scroll') {
      window.addEventListener('scroll', onScroll, { passive: true });
    } else if (mode === 'dwell') {
      dwellTimer = setTimeout(fire, cfg.dwellMs);
    } else if (mode === 'exit') {
      if (noHover) {
        /* No exit intent on touch — fall back to a patient dwell so
           mobile visitors still get one well-spaced introduction. */
        dwellTimer = setTimeout(fire, Math.max(cfg.dwellMs, 30000));
      } else {
        document.addEventListener('mouseout', onMouseOut);
      }
    } else {
      /* both: scroll OR dwell, whichever comes first */
      window.addEventListener('scroll', onScroll, { passive: true });
      dwellTimer = setTimeout(fire, cfg.dwellMs);
    }
  }

  /* ── Init ─────────────────────────────────────────────── */

  function init() {
    el = document.getElementById('AkSpotlight');
    if (!el) return;
    cfg = readConfig();

    recordCurrentProduct(cfg.currentHandle);
    var sessions = sessionCount();

    /* Close is always wired, even if we don't show this time. */
    el.addEventListener('click', function (e) {
      if (e.target.closest('[data-ak-spotlight-close]')) {
        e.preventDefault();
        dismiss();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && el.classList.contains('is-visible')) dismiss();
    });

    watchStickyCart();

    var dataEl = document.querySelector('[data-spotlight-data]');
    var items = readJSON(dataEl ? dataEl.textContent : null, []);

    /* Preview override: show immediately, ignore caps + trigger, and
       don't record a cooldown. */
    if (PREVIEW) {
      var previewPick = chooseProduct(items, cfg.currentHandle) || (items && items[0]);
      if (previewPick) {
        populate(previewPick);
        reveal(true);
      }
      return;
    }

    if (!allowedToShow(sessions)) return;

    var pick = chooseProduct(items, cfg.currentHandle);
    if (!pick) return;

    if (fired) return;
    fired = true;

    armTrigger(function () {
      populate(pick);
      reveal();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
